import { useState } from 'react'
import {
  Box,
  Text,
  Stack,
  Inline,
  Button,
  IconDelete,
  Breadcrumb,
  SelectableList,
  useDialogControllers,
  IconRemoveCircleOutline,
  Alert,
  TextLink,
  useToast
} from '@machobear/design-system'
import { useTranslation, Trans } from 'react-i18next'
import { calc } from '@vanilla-extract/css-utils'
import { useParams } from 'react-router-dom'
import queryString from 'query-string'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { isEmpty, isNil } from 'ramda'
import { vars } from '@machobear/design-system/css'
import { useMutation } from '@tanstack/react-query'
import config from '@/config'
import useSearchParams from '@/hooks/use-search-params'
import { useDebounce } from '@/utils/debounce'
import useProfile from '@/hooks/use-profile'

import client from '@/ports/api-client'
import { LoadingCircle } from '@/components/loading-circle'
import AutoReplyForm from './sub-components/CreateMessageAutoReplyForm'
import RemoveListingsModal from './sub-components/RemoveListingsModal'
import AssignListingsModal from './sub-components/AssignListingsModal'
import AutoReplyPageWrapper from './sub-components/AutoReplyPageWrapper'
import { AssignListingsButton } from './sub-components/AssignListingsButton'
import { snakeToSentence, transformListingData } from './helpers/utils'
import EmptyAttachedList from './sub-components/EmptyAttachedList'
import DeleteMessageModal from './sub-components/DeleteMessageModal'
import ListingsSearchField from './sub-components/ListingsSearchField'
const { WEB_URL } = config
const getAttachedListings = async (
  id: string,
  page?: number,
  filter?: string[],
  search?: string
) => {
  const filterString = filter ? filter.join(',') : ''
  const queryParams = queryString.stringify({
    page,
    filter: filterString,
    search: search
  })

  const url = `/auto_reply_trigger/content/listings/attached/search/${id}?per_page=100${
    queryParams ? `&${queryParams}` : ''
  }`
  const response = await client.get(url)

  return response
}

const CreateEditAutoReply = () => {
  const { t } = useTranslation()
  const { trigger = 'first_tenant_message_received', id }: { trigger?: string; id?: string } =
    useSearchParams()
  let { mode }: { mode: string } = useParams()
  const profileData = useProfile()
  const [searchString, setSearchString] = useState<string>('')
  const [filter, setFilter] = useState<string[]>([])
  const [removeListingIds, setRemoveListingIds] = useState<string[]>([])
  const [contentID, setContentID] = useState<string>(id || '')
  const [pageTitle, setPageTitle] = useState('')
  const [showAssignListings, setShowAssignListings] = useState(!!id)
  const handleShowNextStep = () => setShowAssignListings(true)
  const queryClient = useQueryClient()

  const debouncedSearch = useDebounce(searchString, 700)

  const { fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, data } = useInfiniteQuery({
    queryKey: ['autoreply', 'autoreply_attached_listings', id, debouncedSearch, filter],
    queryFn: ({ pageParam }) => getAttachedListings(String(id), pageParam, filter, debouncedSearch),
    initialPageParam: 1,
    enabled: !!id && (!!searchString || !!filter),
    getNextPageParam: lastPage => {
      return !lastPage.total_items || lastPage.current === lastPage.last ? null : lastPage.next
    }
  })

  const showToast = useToast()
  const attachListingsToAutoReplyMessage = async (body: { listing_ids: string[] }, id: string) => {
    const formData = new FormData()
    formData.append('body', JSON.stringify(body))

    const response = await client.put(`/auto_reply_trigger/content/${id}/listings`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return response
  }

  const attachListings = useMutation({
    mutationFn: ({
      values,
      id
    }: {
      values: {
        listing_ids: string[]
      }
      id: string
    }) => {
      return attachListingsToAutoReplyMessage(values, id)
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['autoreply', 'autoreply_detached_listings']
        }),
        queryClient.invalidateQueries({
          queryKey: ['autoreply', 'autoreply_attached_listings']
        }),
        queryClient.invalidateQueries({ queryKey: ['autoreply', 'autoreply_messages'] })
      ]).catch(error => {
        showToast({
          tone: 'critical',
          message: error?.message
        })
      })
    },
    onError: error => {
      if (error?.message === 'Quota exceeded, please upgrade') {
        showToast({
          tone: 'critical',
          message: t(
            'l.settings.view.auto_reply.max_listings.text',
            'Maximum number of listings reached. Need more? Upgrade or contact support.'
          )
        })
        return
      }
      showToast({
        tone: 'critical',
        message: error?.message
      })
    }
  })

  const handleAttachListings = (selectedListingIds: string[]) => {
    const mutationInput = {
      values: { listing_ids: selectedListingIds },
      id: contentID
    }
    attachListings.mutate(mutationInput)
  }

  const detachListingsToAutoReplyMessage = async (body: { listing_ids: string[] }, id: string) => {
    const formData = new FormData()
    formData.append('body', JSON.stringify(body))

    const response = await client.delete(`/auto_reply_trigger/content/${id}/listings`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return response
  }

  const detachListings = useMutation({
    mutationFn: ({
      values,
      id
    }: {
      values: {
        listing_ids: string[]
      }
      id: string
    }) => {
      return detachListingsToAutoReplyMessage(values, id)
    },

    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['autoreply', 'autoreply_attached_listings']
        }),
        queryClient.invalidateQueries({
          queryKey: ['autoreply', 'autoreply_detached_listings']
        }),
        queryClient.invalidateQueries({ queryKey: ['autoreply', 'autoreply_messages'] })
      ]).catch(error => {
        showToast({
          tone: 'critical',
          message: error?.message
        })
      })
    }
  })
  const handleDetachListings = (selectedListingIds: string[]) => {
    const mutationInput = {
      values: { listing_ids: selectedListingIds },
      id: contentID
    }
    detachListings.mutate(mutationInput)
  }

  const { visible, showModal, closeModal } = useDialogControllers({
    removeListings: false,
    assignListings: false,
    deleteMessage: false
  })
  const transformedItems = data?.pages.flatMap(page => page.items).map(transformListingData)

  const heading = t('l.settings.view.auto_reply.unit_address', 'Unit Address')
  const isEssentialPlan = profileData?.company_info?.base_plan_name === 'essentials-plan-monthly'
  const submitDisabled = Boolean(
    !isEmpty(transformedItems) && !isNil(transformedItems) && isEssentialPlan
  )
  const listHeight = calc.multiply(100, vars.grid)

  return (
    <AutoReplyPageWrapper title={t('l.settings.view.auto_reply.label', 'Auto-Reply')}>
      <Box flexDirection="column" display="flex" gap="small">
        <Box marginBottom={'xxsmall'}>
          <Breadcrumb
            variant="line"
            items={[
              {
                label: t('b.settings.view.settings.title', 'Settings'),
                href: `/settings`
              },
              {
                label: t('l.settings.view.auto_reply.label', 'Auto-Reply'),
                href: `/settings/auto-reply`
              },
              {
                label: `${snakeToSentence(trigger)}`,
                href: `/settings/auto-reply/manage/?trigger=${trigger}`
              },
              {
                label: `${mode === 'create' ? 'Add Auto Reply' : pageTitle}`
              }
            ]}
          />
        </Box>

        <Box display={'flex'} flexDirection="column" alignItems={'flexStart'} gap={'large'}>
          <Box width="full" display="flex" flexDirection="column" gap="large">
            <AutoReplyForm
              onChangeID={id => setContentID(id)}
              pageTitle={pageTitle}
              onChangePageTitle={(title: string) => setPageTitle(title)}
              showNextStep={handleShowNextStep}
            />
            {showAssignListings && (
              <Box
                padding="large"
                border="borderNeutralLight"
                background="surface"
                style={{ borderRadius: '4px 4px 0 0' }}
              >
                <Stack space="large">
                  <Stack space="medium">
                    <Text size="large" weight="strong">
                      {data
                        ? `${t(
                            'l.settings.view.auto_reply.selected_listings',
                            'Selected Listings'
                          )} (${data?.pages[0].total_items})`
                        : `2. ${t(
                            'l.settings.view.auto_reply.assign_listings',
                            'Assign Listings'
                          )}`}
                    </Text>
                    <Text size="small" weight="regular" tone="secondary">
                      {data
                        ? t(
                            'l.settings.view.auto_reply.selected_listings_description',
                            'These are the listings where this auto-reply message will be applied to.'
                          )
                        : t(
                            'l.settings.view.auto_reply.selected_listings_description_empty',
                            'Select the listings where you would like to apply the message.Select the listings where you would like to apply the message.'
                          )}
                    </Text>
                  </Stack>
                  {submitDisabled && (
                    <Alert tone="promote">
                      <Trans i18nKey="l.settings.view.auto_reply.alert_assign_listings" t={t}>
                        <Text size="small">
                          Want to assign more listings?{' '}
                          <TextLink href={`${WEB_URL}/pricing`} target="_blank" weight="regular">
                            Upgrade to Growth Plan
                          </TextLink>{' '}
                          for unlimited access.
                        </Text>
                      </Trans>
                    </Alert>
                  )}
                  <Box>
                    <ListingsSearchField
                      id="searchInput_detachedListings"
                      value={searchString}
                      filters={filter}
                      onFilter={setFilter}
                      onSearch={e => setSearchString(e.target.value)}
                    />
                  </Box>
                  <Box display="flex" flexDirection="column" gap="medium">
                    {isLoading || attachListings.isPending || detachListings.isPending ? (
                      <Box
                        style={{ height: listHeight }}
                        width="full"
                        display="flex"
                        justifyContent="center"
                        padding="xlarge"
                      >
                        <LoadingCircle />
                      </Box>
                    ) : (
                      <SelectableList
                        items={transformedItems || []}
                        heading={heading}
                        itemsSelectedText={t(
                          'l.settings.view.auto_reply.items_selected',
                          'items selected'
                        )}
                        clearAllText={t('l.settings.view.auto_reply.clear_all', 'Clear all')}
                        emptyComponent={<EmptyAttachedList heading={heading} />}
                        hasNextPage={hasNextPage}
                        isFetchingNextPage={isFetchingNextPage}
                        fetchNextPage={fetchNextPage}
                        bulkActions={[
                          {
                            label: t('common.remove', 'Remove'),
                            icon: <IconRemoveCircleOutline />,
                            onClick: selectedData => {
                              setRemoveListingIds(selectedData)
                              showModal('removeListings')()
                            }
                          }
                        ]}
                      />
                    )}

                    <AssignListingsButton
                      contentID={contentID}
                      showModal={showModal}
                      disabled={submitDisabled}
                    />
                  </Box>
                </Stack>
              </Box>
            )}
          </Box>
        </Box>
        {!!id && (
          <Inline space="medium" align="right">
            <Button
              type="button"
              variant="transparent"
              tone="critical"
              icon={<IconDelete />}
              onClick={() => {
                showModal('deleteMessage')()
              }}
            >
              {t('l.settings.view.auto_reply.delete_auto_reply', 'Delete auto reply')}
            </Button>
          </Inline>
        )}
      </Box>

      <RemoveListingsModal
        listingsIds={removeListingIds}
        visible={visible.removeListings}
        closeModal={closeModal('removeListings')}
        trigger={trigger}
        onDetachListings={handleDetachListings}
      />

      <AssignListingsModal
        visible={visible.assignListings}
        closeModal={closeModal('assignListings')}
        contentID={contentID}
        page="1"
        onAttachListings={handleAttachListings}
      />

      <DeleteMessageModal
        visible={visible.deleteMessage}
        closeModal={closeModal('deleteMessage')}
        contentID={contentID}
        trigger={trigger}
      />
    </AutoReplyPageWrapper>
  )
}

export default CreateEditAutoReply
